import { Content, Flex, usePlatformInfo, usePlatformProps } from '@acsbe/ui';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import {
  useApplicationViewState,
  VIEW_STATES,
} from '../../../context/ApplicationViewStateContext';
import { useBilling } from '../../../context/BillingContext';
import { useDomainSelection } from '../../../context/DomainSelectionContext';
import { useMerchantSession } from '../../../context/MerchantSessionContext';
import { MESSAGE_EVENT, sendMessageToWrapper } from '../../../utils';
import { CMS, CMS_CUSTOM_DISCOUNTS } from '../../../utils/constants';
import { Logger } from '../../../utils/logger';
import { BorderedFlex } from './common';
import { ContactUsPlanButton } from './ContactUsButton';
import { CurrentPlanLabel } from './CurrentPlanLabel';
import { BillingCycle } from './Plan';
import {
  maximumFeaturesCount,
  PlanFeature,
  planFeaturesIndexMap,
} from './plan-features';
import { PlanFeatureList } from './PlanFeatureList';
import { PlanPricingDisplay } from './PlanPricingDisplay';
import { SubscribePlanButton } from './SubscribePlanButton';
import { PlanType, PricingModel } from './types';
import { usePlanPricing } from './usePlanPricing';

export interface PlanSummaryProps {
  plan: PlanType;
  isCurrent: boolean;
  isRenew: boolean;
  isReactivate: boolean;
  isUpgrade: boolean;
  isContact: boolean;
  billingCycle: BillingCycle;
  hide?: boolean;
  pageCount?: number;
}

export const PlanSummary = ({
  plan,
  isCurrent,
  isRenew,
  isReactivate,
  isUpgrade,
  isContact,
  billingCycle,
  pageCount,
}: PlanSummaryProps) => {
  // const { onEvent } = useContext(ShopifyContext);
  const p = usePlatformProps();
  const { uptoTablet, uptoPhablet, uptoDesktop } = usePlatformInfo();

  const { merchantDetails } = useMerchantSession();

  const {
    accountId,
    licenseId,
    licenseDetails,
    setPaymentOverlayMessage,
    refreshLicenseDetails,
    isCustomDiscount,
  } = useDomainSelection();

  const { currentAccessibeAccount } = useDomainSelection();
  const isPartner = currentAccessibeAccount?.partner;

  const discountEnabled =
    isPartner && !licenseDetails?.discountEnabled ? false : true;

  const [isLoading, setIsLoading] = useState(false);

  const { setSelectedPlan } = useBilling();

  const LimitationText = () => {
    if (plan.pricingModelPlan === PricingModel.pageCount) {
      return plan.name?.toLowerCase() !== 'enterprise' ? (
        <>
          Up to <StyledSpan hasColor>{plan.limitation / 1000}K</StyledSpan>{' '}
          pages
        </>
      ) : (
        <>
          Above <StyledSpan hasColor>100K</StyledSpan> pages
        </>
      );
    } else {
      return plan.name?.toLowerCase() !== 'enterprise' ? (
        <>
          Up to{' '}
          <StyledSpan hasColor>{plan.visitsLimitation / 1000}K</StyledSpan>{' '}
          monthly visits
        </>
      ) : (
        <>
          Over <StyledSpan hasColor>100K</StyledSpan> monthly visits
        </>
      );
    }
  };

  const PlanPriceSummary = () => {
    //@ts-ignore
    return (
      <Flex style={{ height: '37px', justifyContent: 'start' }}>
        <Content
          size="sm"
          emphasis="none"
          lineHeight={1.43}
          scale="alternative"
          // {...p.fromDesktopXs({ align: 'center' })}
          {...p.uptoPhablet({ align: 'center' })}
        >
          <LimitationText />
        </Content>
      </Flex>
    );
  };

  const [features, setFeatures] = useState<PlanFeature[]>([]);
  const { showPlanComparisonView } = useApplicationViewState();
  useEffect(() => {
    if (!plan || !licenseDetails) return;
    const features: { [key: number]: PlanFeature } = planFeaturesIndexMap(
      plan.tier,
      licenseDetails.pricingModel || '',
      plan.name?.toLowerCase() === 'enterprise',
    );

    if (showPlanComparisonView) {
      const featuresCount = maximumFeaturesCount(
        licenseDetails.pricingModel || '',
      );
      for (let i = 0; i < featuresCount; i++) {
        if (!features[i]) {
          features[i] = {
            title: '',
            info: '',
          };
        }
      }
    }

    const featuresArray = Object.values(features);
    setFeatures(featuresArray);
  }, [plan, licenseDetails, showPlanComparisonView]);

  const { discountedPrice, originalPrice, savings } = usePlanPricing(
    plan,
    billingCycle,
    licenseDetails,
  );

  // Check if there's an actual meaningful discount to display
  const hasActualDiscount = useMemo(() => {
    return (
      discountEnabled &&
      savings &&
      discountedPrice &&
      originalPrice &&
      discountedPrice !== originalPrice
    );
  }, [discountEnabled, savings, discountedPrice, originalPrice]);

  const subscriptionPrice = useMemo(() => {
    if (!plan || !billingCycle || plan.name?.toLowerCase() === 'enterprise') {
      return;
    }

    return licenseDetails?.customPrice
      ? licenseDetails?.customPrice
      : billingCycle === 'annually'
        ? plan.price
        : plan.monthlyPrice;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, billingCycle]);

  const { setApplicationViewState } = useApplicationViewState();

  const onBuyClick = async (
    source: string | undefined,
    uniqueStoreId: string | undefined,
    redirectUrl: string,
    plan: PlanType,
    billingCycle: BillingCycle,
  ) => {
    if (licenseDetails?.licenseType?.toLowerCase() === 'standard') {
      setIsLoading(true);
      Logger.log('Redirecting to dashboard for license management');
      if (
        source &&
        (licenseDetails?.status === 'active' ||
          licenseDetails?.status === 'grace') &&
        source !== licenseDetails?.pluginInstallSource
      ) {
        Logger.log(
          'Redirecting to dashboard for license management due to mismatch in pluginInstallSource',
        );
        // if the source is different from the plugin install source, we should redirect to the dashboard
        window.open(
          `${import.meta.env.VITE_DASHBOARD_URL}/app/widget-license/${licenseId}?switchAccount=${accountId}&plan-id=${plan.id}&billing-cycle=${billingCycle}&redirect-url=${redirectUrl}`,
          '_blank',
        );
      } else if (source && source === CMS.WOOCOMMERCE) {
        const defaultReturnUrl =
          window.location != window.parent.location
            ? document.referrer
            : document.location.href;
        const paymentCompleteUrl = new URL(redirectUrl ?? defaultReturnUrl);
        paymentCompleteUrl.searchParams.set('payment', 'woocom');
        setPaymentOverlayMessage(
          'please wait while we redirect you to the payment page.',
        );
        try {
          const res = await axios.post(
            '/api/accessibe/woo/create-subscription',
            {
              planId: plan.id,
              billingCycle,
              licenseId,
              returnUrl: paymentCompleteUrl.toString(),
            },
            {
              withCredentials: true,
            },
          );
          Logger.log('Response from payment link', res.data);

          sendMessageToWrapper(MESSAGE_EVENT.REDIRECT_TO_URL, {
            url: res.data.confirmation_url,
          });
        } catch (error) {
          setPaymentOverlayMessage('');
        }
      } else if (source && source === CMS.WIX) {
        const instanceId = uniqueStoreId || '';
        window.open(
          `https://www.wix.com/apps/upgrade/${import.meta.env.VITE_WIX_APP_ID}?appInstanceId=${instanceId}`,
          '_blank',
        );
      } else {
        if (
          licenseDetails?.status === 'cancelled' &&
          licenseDetails?.plan?.id === plan?.id &&
          ((billingCycle === 'monthly' && licenseDetails?.monthly) ||
            (billingCycle === 'annually' && !licenseDetails?.monthly))
        ) {
          await axios.post(
            '/api/stripe/reactivate-subscription',
            {
              licenseId: licenseDetails?.id,
            },
            {
              withCredentials: true,
            },
          );
          refreshLicenseDetails();
        } else {
          setPlanContextFromPlan(plan, billingCycle);
          setApplicationViewState(VIEW_STATES.BILLING);
        }
      }
      setIsLoading(false);
    }
  };

  // Helper to set all plan context fields from a PlanType and billingCycle
  const setPlanContextFromPlan = (
    plan: PlanType,
    billingCycle: BillingCycle,
  ) => {
    const planData = {
      planId: plan.id,
      planName: plan.name,
      monthly: billingCycle === 'monthly',
      limitation: plan.limitation,
      visitsLimitation: plan.visitsLimitation,
      pricingModelPlan: plan.pricingModelPlan,
    };
    setSelectedPlan(planData);
  };

  const SubscribeCurrentPlanSection = () => {
    const { redirectUrl: sessionRedirectUrl, merchantDetails } =
      useMerchantSession();
    const redirectUrl = sessionRedirectUrl || document.referrer;
    //const redirectUrl = useMerchantSession().redirectUrl || document.referrer;
    if (isCurrent) {
      return <CurrentPlanLabel />;
    } else if (isContact) {
      return <ContactUsPlanButton />;
    }

    if (plan.name?.toLowerCase() !== 'enterprise') {
      return (
        //@ts-ignore
        <SubscribePlanButton
          isRenew={isRenew}
          isUpgrade={isUpgrade}
          isReactivate={isReactivate}
          aria-describedby="link-context"
          //@ts-ignore
          disabled={
            (pageCount != null &&
              plan.pricingModelPlan === PricingModel.pageCount &&
              pageCount > plan.limitation) ||
            isLoading ||
            licenseDetails?.licenseType?.toLowerCase() !== 'standard'
          }
          onClick={async () => {
            await onBuyClick(
              merchantDetails?.source,
              merchantDetails?.storeId,
              redirectUrl,
              plan,
              billingCycle,
            );
            return {};
          }}
          //@ts-ignore
          dataMixpanel={`subscribe-to-plan-${plan.name
            ?.split(' ')
            ?.join('-')
            ?.toLowerCase()}`}
        />
      );
    } else {
      return <ContactUsPlanButton />;
    }
  };

  return (
    <Flex
      paddingBlock={4}
      gap={4}
      paddingInline={showPlanComparisonView ? 0 : 6}
      radiusTopLeft="xl"
      radiusTopRight="xl"
      position="relative"
    >
      <Flex
        id="link-context"
        align={uptoPhablet ? 'center' : 'flex-start'}
        gap={4}
        self="stretch"
        // row={uptoTablet}
        paddingInline={showPlanComparisonView ? 6 : 0}
      >
        {isCustomDiscount && (
          <Flex
            height="24px"
            scale="premium"
            radius="md"
            gap={1}
            paddingBlock={2}
            paddingInline={1.5}
            justify="center"
            self="stretch"
          >
            <StyledContent
              scale="premium"
              size="sm"
              align="center"
              emphasis="low"
              lineHeight={1}
            >
              {billingCycle === 'annually'
                ? `Save ${
                    licenseDetails?.pricingModel === 'visitsCount'
                      ? '30%'
                      : '20%'
                  } yearly + Extra ${merchantDetails?.source && CMS_CUSTOM_DISCOUNTS[merchantDetails?.source]}% off`
                : `Extra ${merchantDetails?.source && CMS_CUSTOM_DISCOUNTS[merchantDetails?.source]}% off`}
            </StyledContent>
          </Flex>
        )}
        <Flex>
          <BorderedFlex
            align={uptoPhablet ? 'center' : 'flex-start'}
            gap={1}
            self="stretch"
            justify="center"
          >
            <Flex align={uptoPhablet ? 'center' : 'flex-start'} gap={1}>
              <PlanPriceSummary />
            </Flex>
            <Content scale="base" size="xxl" emphasis="low" lineHeight={1.2}>
              {
                //@ts-ignore
                plan.name
              }
            </Content>
          </BorderedFlex>
          <Flex row align="flex-end" gap={1}>
            <PlanPricingDisplay
              plan={plan}
              billingCycle={billingCycle}
              discountedPrice={discountedPrice}
              originalPrice={originalPrice}
              subscriptionPrice={subscriptionPrice}
              hasActualDiscount={!!hasActualDiscount}
              licenseDetails={licenseDetails}
            />
          </Flex>
        </Flex>

        <SubscribeCurrentPlanSection />
      </Flex>
      <PlanFeatureList features={features} />
      {/* {!uptoTablet && getPlanPriceSummary(plan, true)} */}
    </Flex>
  );
};

const StyledSpan = styled.span<{ hasColor?: boolean }>`
  color: ${(props) => (props.hasColor ? '#075FE4' : '')};
  font-weight: 700;
`;

const StyledContent = styled(Content)`
  font-size: 12px;
`;
